<template>
    <div>
        <div v-if="loading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="sr-only">{{ trans('Loading...') }}</span>
            </div>
        </div>

        <div v-else-if="imports.length === 0" class="text-center text-muted py-3">
            {{ trans('No previous imports found') }}
        </div>

        <div v-else class="table-responsive">
            <table class="table table-sm">
                <thead>
                    <tr>
                        <th>{{ trans('File') }}</th>
                        <th>{{ trans('Date') }}</th>
                        <th>{{ trans('Rows') }}</th>
                        <th>{{ trans('Status') }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="importItem in imports" :key="importItem.id">
                        <td>
                            <a :href="getShowUrl(importItem.id)" class="text-truncate d-inline-block" style="max-width: 150px;" :title="importItem.original_filename">
                                {{ importItem.original_filename }}
                            </a>
                        </td>
                        <td>{{ formatDate(importItem.created_at) }}</td>
                        <td>
                            <span v-if="importItem.status === 'completed'">
                                {{ importItem.successful_rows }}/{{ importItem.total_rows }}
                            </span>
                            <span v-else-if="importItem.status === 'processing'">
                                {{ importItem.processed_rows }}/{{ importItem.total_rows }}
                            </span>
                            <span v-else>
                                {{ importItem.total_rows || '-' }}
                            </span>
                        </td>
                        <td>
                            <span class="badge" :class="getStatusBadgeClass(importItem.status)">
                                {{ getStatusLabel(importItem.status) }}
                            </span>
                        </td>
                        <td class="text-right">
                            <a :href="getShowUrl(importItem.id)" class="text-muted">
                                <i class="icofont-eye-alt"></i>
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>

            <nav v-if="meta.last_page > 1" aria-label="Import history pagination">
                <ul class="pagination pagination-sm justify-content-center mb-0">
                    <li class="page-item" :class="{ disabled: meta.current_page === 1 }">
                        <a class="page-link" href="#" @click.prevent="loadPage(meta.current_page - 1)">
                            &laquo;
                        </a>
                    </li>
                    <li class="page-item" v-for="page in visiblePages" :key="page" :class="{ active: page === meta.current_page }">
                        <a class="page-link" href="#" @click.prevent="loadPage(page)">
                            {{ page }}
                        </a>
                    </li>
                    <li class="page-item" :class="{ disabled: meta.current_page === meta.last_page }">
                        <a class="page-link" href="#" @click.prevent="loadPage(meta.current_page + 1)">
                            &raquo;
                        </a>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        listUrl: {
            type: String,
            required: true,
        },
        entityType: {
            type: String,
            required: true,
        },
        showUrl: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            imports: [],
            meta: {
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            },
        };
    },

    computed: {
        visiblePages() {
            const pages = [];
            const start = Math.max(1, this.meta.current_page - 2);
            const end = Math.min(this.meta.last_page, this.meta.current_page + 2);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        },
    },

    mounted() {
        this.loadImports();
    },

    methods: {
        async loadImports() {
            this.loading = true;

            try {
                const response = await axios.get(this.listUrl, {
                    params: { page: this.meta.current_page },
                });

                this.imports = response.data.data;
                this.meta = response.data.meta;
            } catch (error) {
                console.error('Failed to load imports:', error);
            } finally {
                this.loading = false;
            }
        },

        loadPage(page) {
            if (this.loading) return;
            if (page >= 1 && page <= this.meta.last_page) {
                this.meta.current_page = page;
                this.loadImports();
            }
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        },

        getStatusBadgeClass(status) {
            const classes = {
                pending: 'badge-secondary',
                processing: 'badge-info',
                completed: 'badge-success',
                failed: 'badge-danger',
                cancelled: 'badge-warning',
            };
            return classes[status] || 'badge-secondary';
        },

        getStatusLabel(status) {
            const labels = {
                pending: this.trans('Pending'),
                processing: this.trans('Processing'),
                completed: this.trans('Completed'),
                failed: this.trans('Failed'),
                cancelled: this.trans('Cancelled'),
            };
            return labels[status] || status;
        },

        getShowUrl(importId) {
            return this.showUrl.replace(':import', importId);
        },
    },
};
</script>
